import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Pencil, Power, Search, Plus, History, Building2, Eye, ArrowUp, ArrowDown, ChevronsUpDown, ShieldAlert, CalendarClock, AlertTriangle } from 'lucide-react';
import CustomerForm from './CustomerForm';
import CustomerDetail from './CustomerDetail';
import { StatusReasonDialog, HistoryDialog } from './EntityDialogs';
import { canWriteCustomers, canReactivateMaster, isOwner } from '@/lib/workspaces';
import { useTableStandard, TableToolbar } from '@/lib/tableStandard';
import { OwnerPurgeDialog } from '@/components/admin/OwnerPurgeDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { MobileRecordCard, MobileRecordList } from '@/components/common/mobile/MobileRecordCard';
import { MobileSortControl } from '@/components/common/mobile/MobileSortControl';

const StatusPill = ({ status }) => {
  const active = (status || 'active') === 'active';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${active ? 'bg-[#EAF6DD] text-[#4f8d14]' : 'bg-[#FBF3E4] text-[#C8641E]'}`}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
};

const CustomerManager = ({ openCustomerId = null }) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const { token, user } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const canManage = canWriteCustomers(user);
  const canReactivate = canReactivateMaster(user);
  const owner = isOwner(user);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [formCustomer, setFormCustomer] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [detailInitialTab, setDetailInitialTab] = useState('overview');
  const [expiring, setExpiring] = useState(null);
  const [showExpiring, setShowExpiring] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/customers`, authHeaders).then((r) => setItems(r.data || [])).catch(() => {});
    axios.get(`${API}/service-agreements/expiring`, authHeaders).then((r) => setExpiring(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCustomerDetail = (cust, tab = 'overview') => { setDetailInitialTab(tab); setDetailCustomer(cust); };
  const openFromAgreement = (ev) => {
    const found = items.find((c) => c.id === ev.customer_id);
    if (found) { setShowExpiring(false); openCustomerDetail(found, 'agreements'); }
  };
  const alertTotal = expiring ? (expiring.counts.expiring_soon + expiring.counts.expired + expiring.counts.notice + expiring.counts.needs_review) : 0;

  // Deep link from other apps (e.g. Contract Budgets) — open that customer's
  // Master detail record once the list has loaded.
  const [deepOpened, setDeepOpened] = useState(false);
  useEffect(() => {
    if (openCustomerId && !deepOpened && items.length) {
      const found = items.find((c) => c.id === openCustomerId);
      if (found) { setDetailCustomer(found); setDeepOpened(true); }
    }
  }, [openCustomerId, deepOpened, items]);

  const searchFiltered = items.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${c.name || ''} ${c.industry || ''} ${c.main_contact_name || ''} ${c.account_number || ''} ${c.account_code || ''}`.toLowerCase().includes(q);
  });

  const std = useTableStandard(searchFiltered, { defaultSort: { key: 'name', dir: 1 } });
  const SortHead = ({ label, sortKey, className = '' }) => {
    const active = std.sort.key === sortKey;
    const next = () => std.setSort((s) => (s.key !== sortKey ? { key: sortKey, dir: 1 } : s.dir === 1 ? { key: sortKey, dir: -1 } : { key: null, dir: 0 }));
    return (
      <TableHead className={`sticky top-0 z-10 bg-gray-50 ${className}`}>
        <button type="button" onClick={next} data-testid={`sort-${sortKey}`}
          className="inline-flex items-center gap-1 select-none hover:text-[#0E2A47]">
          {label}
          {active ? (std.sort.dir === 1 ? <ArrowUp size={13} className="text-[#8FC93A]" /> : <ArrowDown size={13} className="text-[#8FC93A]" />) : <ChevronsUpDown size={13} className="text-slate-300" />}
        </button>
      </TableHead>
    );
  };
  const FilterHead = ({ field, className = '' }) => (
    <TableHead className={`sticky top-0 z-10 bg-gray-50 ${className}`}>
      <input value={std.filters[field] || ''} onChange={(e) => std.setFilter(field, e.target.value)}
        placeholder="Filter…" data-testid={`customer-colfilter-${field}`}
        className="w-full min-w-[70px] rounded border border-gray-200 px-2 py-1 text-xs font-normal normal-case" />
    </TableHead>
  );

  if (detailCustomer) {
    return (
      <>
        <CustomerDetail customer={detailCustomer} canManage={canManage} initialTab={detailInitialTab}
          onBack={() => { setDetailCustomer(null); setDetailInitialTab('overview'); }}
          onEdit={() => setFormCustomer(detailCustomer)} />
        {formCustomer !== undefined && (
          <CustomerForm customer={formCustomer}
            onClose={() => setFormCustomer(undefined)}
            onSaved={() => { load(); setFormCustomer(undefined); }} />
        )}
      </>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Operations</div>
          <h1 className="text-2xl font-extrabold text-[#0E2A47] sm:text-3xl">Customers</h1>
          <p className="text-sm text-slate-500 mt-1">Master customer records — {canManage ? 'create, edit or deactivate.' : 'read-only view.'}</p>
        </div>
        {canManage && (
          <button onClick={() => setFormCustomer(null)} data-testid="add-customer-btn"
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] max-sm:min-h-[44px] max-sm:w-full sm:shrink-0">
            <Plus size={16} /> Add Customer
          </button>
        )}
      </div>

      {alertTotal > 0 && (
        <button onClick={() => setShowExpiring(true)} data-testid="expiring-agreements-btn"
          className="mb-4 flex w-full items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left transition hover:border-amber-300">
          <span className="flex items-center gap-2 text-sm font-semibold text-amber-800">
            <CalendarClock size={17} /> Service agreements need attention
          </span>
          <span className="flex flex-wrap items-center gap-1.5 text-xs">
            {expiring.counts.expired > 0 && <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700" data-testid="expiring-count-expired">{expiring.counts.expired} expired</span>}
            {expiring.counts.expiring_soon > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700" data-testid="expiring-count-soon">{expiring.counts.expiring_soon} expiring ≤45d</span>}
            {expiring.counts.notice > 0 && <span className="rounded-full bg-indigo-100 px-2 py-0.5 font-semibold text-indigo-700">{expiring.counts.notice} renewal notice</span>}
            {expiring.counts.needs_review > 0 && <span className="rounded-full bg-orange-100 px-2 py-0.5 font-semibold text-orange-700">{expiring.counts.needs_review} date review</span>}
          </span>
        </button>
      )}

      <div className="relative max-w-sm mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder="Search customers…" value={search}
          onChange={(e) => setSearch(e.target.value)} data-testid="customer-search" />
      </div>

      <TableToolbar std={std} total={items.length} noun="customer" idPrefix="customers" />

      {/* Mobile (<lg): structured record cards. Desktop table preserved below. */}
      <div className="lg:hidden">
        <div className="mb-3">
          <MobileSortControl sort={std.sort} setSort={std.setSort} testid="customer-sort-m" options={[
            { key: 'name', label: 'Customer Name' },
            { key: 'account_number', label: 'Account #' },
            { key: 'account_code', label: 'Account Code' },
            { key: 'industry', label: 'Industry' },
            { key: 'status', label: 'Status' },
            { key: 'main_contact_name', label: 'Main Contact' },
          ]} />
        </div>
        {std.matchCount === 0
          ? <div className="rounded-xl border border-slate-200 bg-white py-16 text-center text-sm text-slate-400">No customers found.</div>
          : (
            <MobileRecordList testid="customers-cards">
              {std.view.map((c) => {
                const active = (c.status || 'active') === 'active';
                const img = (
                  <div className="relative h-11 w-11">
                    {c.logo_url && <img src={`${API}/customers/${c.id}/logo?auth=${token}`} alt={c.name} onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} className="h-11 w-11 rounded-md border border-slate-200 bg-white object-contain p-0.5" data-testid={`customer-logo-m-${c.id}`} />}
                    <div style={{ display: c.logo_url ? 'none' : 'flex' }} className="h-11 w-11 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-slate-300"><Building2 size={18} /></div>
                  </div>
                );
                const actions = (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="rounded-md p-1.5 hover:bg-gray-100" data-testid={`customer-actions-m-${c.id}`}><MoreHorizontal size={18} className="text-slate-500" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canManage && <DropdownMenuItem onClick={() => setFormCustomer(c)} data-testid={`customer-edit-m-${c.id}`}><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => setHistoryTarget(c)} data-testid={`customer-history-m-${c.id}`}><History size={14} className="mr-2" /> View History</DropdownMenuItem>
                      {canManage && active && <DropdownMenuItem onClick={() => setStatusTarget({ entity: c, nextStatus: 'inactive' })} data-testid={`customer-toggle-m-${c.id}`}><Power size={14} className="mr-2" /> Deactivate</DropdownMenuItem>}
                      {!active && canReactivate && <DropdownMenuItem onClick={() => setStatusTarget({ entity: c, nextStatus: 'active' })} data-testid={`customer-toggle-m-${c.id}`}><Power size={14} className="mr-2" /> Reactivate</DropdownMenuItem>}
                      {!canManage && <DropdownMenuItem disabled data-testid={`customer-readonly-m-${c.id}`}><Eye size={14} className="mr-2" /> Read-only access</DropdownMenuItem>}
                      {owner && <DropdownMenuItem onClick={() => setPurgeTarget(c)} data-testid={`customer-purge-m-${c.id}`} className="text-red-600 focus:text-red-700"><ShieldAlert size={14} className="mr-2" /> Purge (Owner only)</DropdownMenuItem>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
                return (
                  <MobileRecordCard key={c.id} testid={`customer-card-${c.id}`}
                    onClick={() => setDetailCustomer(c)}
                    image={img} title={c.name} onTitleClick={() => setDetailCustomer(c)} titleTestid={`customer-open-m-${c.id}`}
                    badge={<StatusPill status={c.status} />}
                    meta={[
                      { label: 'Account #', value: c.account_number || '—' },
                      { label: 'Account Code', value: c.account_code || '—' },
                      { label: 'Industry', value: c.industry ? <span className="capitalize">{c.industry}</span> : '—' },
                      { label: 'Main Contact', value: c.main_contact_name || '—' },
                    ]}
                    actions={actions}
                  />
                );
              })}
            </MobileRecordList>
          )}
      </div>

      <Card className="hidden lg:block">
        <CardContent className="p-0">
            <Table data-testid="customers-table" containerClassName="max-h-[70vh] overflow-auto rounded-md">
              <TableHeader>
                <TableRow className="bg-gray-50">
                <TableHead className="sticky top-0 z-10 bg-gray-50 w-12"></TableHead>
                <SortHead label="Account #" sortKey="account_number" />
                <SortHead label="Account Code" sortKey="account_code" className="hidden sm:table-cell" />
                <SortHead label="Customer Name" sortKey="name" />
                <SortHead label="Industry" sortKey="industry" className="hidden md:table-cell" />
                <SortHead label="Status" sortKey="status" />
                <SortHead label="Main Contact" sortKey="main_contact_name" className="hidden lg:table-cell" />
                <TableHead className="sticky top-0 z-10 bg-gray-50 text-right">Actions</TableHead>
                </TableRow>
                <TableRow className="bg-gray-50">
                <TableHead className="sticky top-0 z-10 bg-gray-50" />
                <FilterHead field="account_number" />
                <FilterHead field="account_code" className="hidden sm:table-cell" />
                <FilterHead field="name" />
                <FilterHead field="industry" className="hidden md:table-cell" />
                <FilterHead field="status" />
                <FilterHead field="main_contact_name" className="hidden lg:table-cell" />
                <TableHead className="sticky top-0 z-10 bg-gray-50" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {std.view.map((c) => {
                  const active = (c.status || 'active') === 'active';
                  return (
                    <TableRow key={c.id} data-testid={`customer-row-${c.id}`}
                      onClick={() => setDetailCustomer(c)}
                      className="cursor-pointer hover:bg-slate-50">
                      <TableCell className="py-1.5">
                        {c.logo_url
                          ? <img src={`${API}/customers/${c.id}/logo?auth=${token}`} alt={c.name} onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} className="h-9 w-9 rounded-md border border-slate-200 bg-white object-contain p-0.5" data-testid={`customer-logo-${c.id}`} />
                          : null}
                        <div style={{ display: c.logo_url ? 'none' : 'flex' }} className="h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-slate-300"><Building2 size={16} /></div>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-600">{c.account_number || '—'}</TableCell>
                      <TableCell className="hidden sm:table-cell font-mono text-xs font-semibold text-[#0E2A47]">{c.account_code || '—'}</TableCell>
                      <TableCell className="font-medium text-slate-800"><button onClick={(e) => { e.stopPropagation(); setDetailCustomer(c); }} data-testid={`customer-open-${c.id}`} className="text-left hover:text-[#0E2A47] hover:underline">{c.name}</button></TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-slate-500 capitalize">{c.industry || '—'}</TableCell>
                      <TableCell><StatusPill status={c.status} /></TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-slate-500">{c.main_contact_name || '—'}{c.main_contact_title ? <span className="block text-xs text-slate-400">{c.main_contact_title}</span> : null}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="rounded-md p-1.5 hover:bg-gray-100" data-testid={`customer-actions-${c.id}`}>
                              <MoreHorizontal size={18} className="text-slate-500" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canManage && (
                              <DropdownMenuItem onClick={() => setFormCustomer(c)} data-testid={`customer-edit-${c.id}`}>
                                <Pencil size={14} className="mr-2" /> Edit
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => setHistoryTarget(c)} data-testid={`customer-history-${c.id}`}>
                              <History size={14} className="mr-2" /> View History
                            </DropdownMenuItem>
                            {canManage && active && (
                              <DropdownMenuItem onClick={() => setStatusTarget({ entity: c, nextStatus: 'inactive' })}
                                data-testid={`customer-toggle-${c.id}`}>
                                <Power size={14} className="mr-2" /> Deactivate
                              </DropdownMenuItem>
                            )}
                            {!active && canReactivate && (
                              <DropdownMenuItem onClick={() => setStatusTarget({ entity: c, nextStatus: 'active' })}
                                data-testid={`customer-toggle-${c.id}`}>
                                <Power size={14} className="mr-2" /> Reactivate
                              </DropdownMenuItem>
                            )}
                            {!canManage && (
                              <DropdownMenuItem disabled data-testid={`customer-readonly-${c.id}`}>
                                <Eye size={14} className="mr-2" /> Read-only access
                              </DropdownMenuItem>
                            )}
                            {owner && (
                              <DropdownMenuItem onClick={() => setPurgeTarget(c)} data-testid={`customer-purge-${c.id}`}
                                className="text-red-600 focus:text-red-700">
                                <ShieldAlert size={14} className="mr-2" /> Purge (Owner only)
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {std.matchCount === 0 && <div className="py-16 text-center text-slate-400 text-sm">No customers found.</div>}
        </CardContent>
      </Card>

      {formCustomer !== undefined && (
        <CustomerForm customer={formCustomer} onClose={() => setFormCustomer(undefined)} onSaved={load} />
      )}
      {statusTarget && (
        <StatusReasonDialog kind="customer" entity={statusTarget.entity} nextStatus={statusTarget.nextStatus}
          onClose={() => setStatusTarget(null)} onDone={load} />
      )}
      {historyTarget && (
        <HistoryDialog kind="customer" entity={historyTarget} onClose={() => setHistoryTarget(null)} />
      )}
      {purgeTarget && (
        <OwnerPurgeDialog entityType="customer" entityId={purgeTarget.id}
          onClose={() => setPurgeTarget(null)} onPurged={load} />
      )}

      <Dialog open={showExpiring} onOpenChange={setShowExpiring}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="expiring-agreements-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarClock size={18} /> Agreements Needing Attention</DialogTitle></DialogHeader>
          {expiring && (
            <div className="space-y-4">
              {[
                { key: 'expired', label: 'Expired', items: expiring.expired, cls: 'text-red-700', dot: 'bg-red-500' },
                { key: 'expiring_soon', label: 'Expiring within 45 days', items: expiring.expiring_soon, cls: 'text-amber-700', dot: 'bg-amber-500' },
                { key: 'notice', label: 'Auto-renew notice deadline', items: expiring.notice, cls: 'text-indigo-700', dot: 'bg-indigo-500' },
                { key: 'needs_review', label: 'Active, no confirmed expiration (needs review)', items: expiring.needs_review, cls: 'text-orange-700', dot: 'bg-orange-500' },
              ].filter((g) => g.items && g.items.length).map((g) => (
                <div key={g.key} data-testid={`expiring-group-${g.key}`}>
                  <div className={`mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide ${g.cls}`}><span className={`h-2 w-2 rounded-full ${g.dot}`} /> {g.label} ({g.items.length})</div>
                  <div className="space-y-1.5">
                    {g.items.map((ev) => (
                      <button key={ev.id} onClick={() => openFromAgreement(ev)} data-testid={`expiring-item-${ev.id}`}
                        className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-2.5 text-left hover:border-[#8FC93A]">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-[#0E2A47]">{ev.customer_name || 'Customer'} <span className="font-normal text-slate-400">· {ev.name}</span></div>
                          <div className="font-mono text-[11px] text-slate-400">{ev.agreement_number}{ev.responsible_admin_name ? ` · ${ev.responsible_admin_name}` : ''}</div>
                        </div>
                        <div className="shrink-0 text-right text-xs">
                          {g.key === 'notice'
                            ? <span className="text-indigo-600">{ev.notice_deadline_date}</span>
                            : g.key === 'needs_review'
                              ? <span className="text-orange-600">No date</span>
                              : <span className={g.key === 'expired' ? 'text-red-600' : 'text-amber-600'}>{ev.expiration_date}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {alertTotal === 0 && <p className="text-sm text-slate-400">Nothing needs attention right now.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerManager;
